/**
 * MA-305 Shared Light Theme Token Layer
 * ═══════════════════════════════════════════════════════════════════
 * 
 * **Purpose**: Temporary coordination layer between legacy LP tokens
 * and MA-304 finalTheme funnel tokens to prevent drift before full
 * LP migration to finalTheme.
 * 
 * **Critical**: These are CURRENT production values. Do NOT change
 * these values without coordinating both LP and Funnel systems.
 * 
 * **Architecture**:
 * - Funnel theme engine (themeEngine.js) consumes these constants
 * - LP token resolver (resolveLPBrandTokens.js) consumes these constants
 * - Both systems stay in sync programmatically
 * 
 * **Future**: When LP fully migrates to finalTheme, this file can be
 * deprecated and values moved into finalTheme presets directly.
 * 
 * ═══════════════════════════════════════════════════════════════════
 */

/**
 * Shared Light Theme token constants
 * 
 * Values derived from current production state (MA-305 audit):
 * - page_bg: Current LP bgPage and Funnel surface_base (both #f8fafc)
 * - surface_1: Current LP surface1 and Funnel surface_elevated (both #ffffff)
 * - surface_2: Current LP surface2 (#f1f5f9)
 * - surface_3: Current LP surface3 (#f8fafc, alias to page_bg)
 * - surface_elevated: Current Funnel surface_elevated (#ffffff)
 * - surface_subtle: Current LP cardBg and Funnel surface_subtle (both #e8eef5)
 * - surface_response: Current Funnel surface_response (#f9fafb, derived from getContrastingShade logic)
 * - card_bg: Current LP cardBg (#e8eef5, same as surface_subtle)
 * 
 * Text colors:
 * - LP uses darker text (#0f172a) for better contrast on light surfaces
 * - Funnel uses slightly lighter text (#1e293b) for softer appearance
 * - This intentional difference is preserved for now
 * 
 * Border colors:
 * - LP uses rgba(15, 23, 42, 0.08) for subtle borders
 * - Funnel uses #e2e8f0 (hex equivalent)
 * - Both are provided as separate keys for backward compatibility
 */
export const SHARED_LIGHT_THEME_TOKENS = {
  // ═══════════════════════════════════════════════════════════════════
  // Surface Colors (Backgrounds)
  // ═══════════════════════════════════════════════════════════════════
  
  /**
   * Page/thread background - lightest surface
   * Used by: LP body background, Funnel thread area
   * Current value: #f8fafc (very light blue-gray)
   */
  page_bg: '#f8fafc',
  
  /**
   * Primary elevated surface - white
   * Used by: LP hero section, Funnel container/card, headers
   * Current value: #ffffff (pure white)
   */
  surface_1: '#ffffff',
  
  /**
   * Secondary surface - light gray
   * Used by: LP trust signals, funnel container wrapper
   * Current value: #f1f5f9 (light slate)
   */
  surface_2: '#f1f5f9',
  
  /**
   * Tertiary surface - alias to page background
   * Used by: LP rarely, mostly for surface variation
   * Current value: #f8fafc (same as page_bg)
   */
  surface_3: '#f8fafc',
  
  /**
   * Elevated surface (semantic alias to surface_1)
   * Used by: Funnel container, card backgrounds
   * Current value: #ffffff (pure white)
   */
  surface_elevated: '#ffffff',
  
  /**
   * Subtle surface - for cards and bot bubbles
   * Used by: LP card backgrounds, Funnel bot message bubbles
   * Current value: #e8eef5 (light blue-gray, aligned in MA-279C)
   */
  surface_subtle: '#e8eef5',
  
  /**
   * Response area background
   * Used by: Funnel response area (previously computed via getContrastingShade)
   * Current value: #f9fafb (very light gray, derived from getContrastingShade(#ffffff))
   * Derivation: getContrastingShade lightens white by small amount for subtle elevation
   */
  surface_response: '#f9fafb',
  
  /**
   * Card background (semantic alias to surface_subtle)
   * Used by: LP card sections
   * Current value: #e8eef5 (same as surface_subtle)
   */
  card_bg: '#e8eef5',
  
  /**
   * Elevated background for LP sections
   * Used by: LP elevated sections
   * Current value: #f1f5f9 (same as surface_2)
   */
  elevated_bg: '#f1f5f9',
  
  /**
   * Header background (MA-305 Phase 0)
   * Purpose: Semantic control over funnel header (independent from container/bot bubble)
   * Initial value: #ffffff (preserves current majority behavior - white header)
   * Used by: All funnel headers across all surfaces
   * Note: Value may be adjusted in Phase 1 (visual spec), but Phase 0 preserves current visual
   */
  surface_header: '#ffffff',
  
  // ═══════════════════════════════════════════════════════════════════
  // Text Colors
  // ═══════════════════════════════════════════════════════════════════
  
  /**
   * LP primary text - darker for better contrast
   * Used by: LP headlines, body text
   * Current value: #0f172a (very dark slate)
   * Note: Intentionally darker than Funnel text
   */
  text_primary_lp: '#0f172a',
  
  /**
   * LP secondary text
   * Used by: LP subheadlines, descriptions
   * Current value: #475569 (medium slate)
   */
  text_secondary_lp: '#475569',
  
  /**
   * LP muted text
   * Used by: LP hints, captions
   * Current value: #94a3b8 (light slate)
   */
  text_muted_lp: '#94a3b8',
  
  /**
   * Funnel primary text - slightly lighter for softer appearance
   * Used by: Funnel bot message text, headers
   * Current value: #1e293b (dark slate, lighter than LP)
   * Note: Intentional difference from LP text
   */
  text_primary_funnel: '#1e293b',
  
  /**
   * Funnel secondary text
   * Used by: Funnel secondary text, placeholders
   * Current value: #64748b (slate)
   */
  text_secondary_funnel: '#64748b',
  
  /**
   * Text on inverse (dark) backgrounds
   * Used by: Text on accent buttons, dark surfaces
   * Current value: #ffffff (pure white)
   */
  text_inverse: '#ffffff',
  
  // ═══════════════════════════════════════════════════════════════════
  // Border Colors
  // ═══════════════════════════════════════════════════════════════════
  
  /**
   * Subtle border (hex format) - used by Funnel
   * Current value: #e2e8f0 (light slate border)
   */
  border_subtle_hex: '#e2e8f0',
  
  /**
   * Subtle border (rgba format) - used by LP
   * Current value: rgba(15, 23, 42, 0.08) (semi-transparent dark slate)
   * Note: Slightly different from hex version for LP styling
   */
  border_subtle_rgba: 'rgba(15, 23, 42, 0.08)',
  
  /**
   * Strong border
   * Used by: Emphasized borders, focus states
   * Current value: #cbd5e1 (medium slate border)
   */
  border_strong: '#cbd5e1',
  
  // ═══════════════════════════════════════════════════════════════════
  // Notes on Dynamic Values (NOT included here)
  // ═══════════════════════════════════════════════════════════════════
  
  /**
   * Accent colors (primary, CTA buttons) remain DYNAMIC and are NOT
   * included in this shared layer. They come from:
   * - LP: Brand configuration accent
   * - Funnel: Theme preset primary color
   * 
   * These continue to be resolved at runtime based on user selection.
   */
};

/**
 * Helper function to get all surface tokens as an object
 * Useful for debugging and validation
 */
export function getSurfaceTokens() {
  return {
    page_bg: SHARED_LIGHT_THEME_TOKENS.page_bg,
    surface_1: SHARED_LIGHT_THEME_TOKENS.surface_1,
    surface_2: SHARED_LIGHT_THEME_TOKENS.surface_2,
    surface_3: SHARED_LIGHT_THEME_TOKENS.surface_3,
    surface_elevated: SHARED_LIGHT_THEME_TOKENS.surface_elevated,
    surface_subtle: SHARED_LIGHT_THEME_TOKENS.surface_subtle,
    surface_response: SHARED_LIGHT_THEME_TOKENS.surface_response,
    card_bg: SHARED_LIGHT_THEME_TOKENS.card_bg,
    elevated_bg: SHARED_LIGHT_THEME_TOKENS.elevated_bg,
  };
}

/**
 * Helper function to get all text tokens as an object
 */
export function getTextTokens() {
  return {
    text_primary_lp: SHARED_LIGHT_THEME_TOKENS.text_primary_lp,
    text_secondary_lp: SHARED_LIGHT_THEME_TOKENS.text_secondary_lp,
    text_muted_lp: SHARED_LIGHT_THEME_TOKENS.text_muted_lp,
    text_primary_funnel: SHARED_LIGHT_THEME_TOKENS.text_primary_funnel,
    text_secondary_funnel: SHARED_LIGHT_THEME_TOKENS.text_secondary_funnel,
    text_inverse: SHARED_LIGHT_THEME_TOKENS.text_inverse,
  };
}

/**
 * Helper function to get all border tokens as an object
 */
export function getBorderTokens() {
  return {
    border_subtle_hex: SHARED_LIGHT_THEME_TOKENS.border_subtle_hex,
    border_subtle_rgba: SHARED_LIGHT_THEME_TOKENS.border_subtle_rgba,
    border_strong: SHARED_LIGHT_THEME_TOKENS.border_strong,
  };
}

/**
 * Validation function to check token parity between LP and Funnel
 * Returns true if critical surface tokens match
 */
export function validateTokenParity() {
  const issues = [];
  
  // Check that LP bgPage matches Funnel surface_base
  if (SHARED_LIGHT_THEME_TOKENS.page_bg !== SHARED_LIGHT_THEME_TOKENS.page_bg) {
    issues.push('page_bg mismatch');
  }
  
  // Check that LP surface1 matches Funnel surface_elevated
  if (SHARED_LIGHT_THEME_TOKENS.surface_1 !== SHARED_LIGHT_THEME_TOKENS.surface_elevated) {
    issues.push('surface_1/surface_elevated mismatch');
  }
  
  // Check that LP cardBg matches Funnel surface_subtle
  if (SHARED_LIGHT_THEME_TOKENS.card_bg !== SHARED_LIGHT_THEME_TOKENS.surface_subtle) {
    issues.push('card_bg/surface_subtle mismatch');
  }
  
  return {
    valid: issues.length === 0,
    issues,
    note: 'Text color differences are intentional (LP darker, Funnel lighter)'
  };
}
